import { Tooltip } from '@design/components/tooltip/Tooltip';
import clsx from 'clsx';
import { useMemo, useRef } from 'react';

import {
  MultiLineChartLine,
  TooltipData,
  TooltipSort,
} from '../../MultiLineChart.types';
import styles from './MultiLineChartTooltip.module.scss';

export type MultiLineChartTooltipProps = {
  lines: MultiLineChartLine[];
  tooltipData: TooltipData;
  formatter: (n: number) => string;
  sortBy?: TooltipSort;
  offsetY?: number;
  notAvailableLabel: string;
  locale?: string;
  isBreakdown?: boolean;
  includeTime?: boolean;
};

const MIN_Y_VALUE_FOR_BREAKDOWN = 12;

export const MultiLineChartTooltip = ({
  lines,
  tooltipData,
  formatter,
  sortBy,
  offsetY = 0,
  notAvailableLabel,
  locale,
  isBreakdown = false,
  includeTime = false,
}: MultiLineChartTooltipProps) => {
  const minYRef = useRef<number | undefined>(undefined);

  const shiftOptions = useMemo(
    () => ({
      mainAxis: false,
      crossAxis: true,
      limiter: {
        fn: (state: { x: number; y: number }) => {
          const { y } = state;

          const numberOfAssets = lines.length;

          if (minYRef.current === undefined) {
            minYRef.current = y;
          }

          const yValueToUse =
            isBreakdown && numberOfAssets >= 8 ? MIN_Y_VALUE_FOR_BREAKDOWN : y;

          return {
            x: state.x,
            y: yValueToUse,
          };
        },
      },
    }),
    []
  );

  const tooltipRows = lines.map(line => ({
    color: line.color,
    label: line.label,
    value: line.data.find(d => d.date.valueOf() === tooltipData.date.valueOf())
      ?.value,
    isBenchmark: line.isBenchmark,
  }));

  if (sortBy) {
    tooltipRows.sort((a, b) =>
      sortBy === 'desc'
        ? (b.value ?? 0) - (a.value ?? 0)
        : (a.value ?? 0) - (b.value ?? 0)
    );
  }

  const formatDate = (date: Date) =>
    date.toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...(includeTime && {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }),
    });

  const TooltipLabel = (
    <div
      className={styles.tooltipLabelContainer}
      data-testid="multi-line-chart-tooltip"
    >
      <span className={styles.dateLabel}>{formatDate(tooltipData.date)}</span>
      {tooltipRows.map(row => (
        <div
          className={styles.tooltipRow}
          key={row.label}
          data-testid="tooltip-row"
        >
          <div className={styles.tooltipLegend}>
            <div
              className={clsx(styles.tooltipLegendColor, {
                [styles.tooltipLegendColorDashed]: row.isBenchmark,
              })}
              style={{
                backgroundColor: row.isBenchmark ? undefined : row.color,
                borderBottomColor: row.isBenchmark ? row.color : undefined,
              }}
            />
            <div>{row.label}</div>
          </div>
          <div data-testid="row-value-label">
            {row.value !== undefined ? formatter(row.value) : notAvailableLabel}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Tooltip
      className={styles.tooltip}
      inPortal
      label={TooltipLabel}
      isTouchDevice={false}
      isOpen
      hasArrow={false}
      offsetY={offsetY}
      dismissible={false}
      shiftOptions={shiftOptions}
      strategy="fixed"
      placement="right"
    >
      <div
        className={styles.tooltipRoot}
        style={{ left: `${tooltipData.xPos}px` }}
      />
    </Tooltip>
  );
};
